/**
 * Background Renderers
 * 
 * Collection of 25+ background rendering functions for the drawing canvas.
 * Each function renders a different pattern or style to the canvas context.
 * 
 * All renderers follow the same signature:
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {Object} cache - Optional cache object for stateful backgrounds (like galaxy stars)
 */

export const renderWhiteBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
};

export const renderBlueprintBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#183a5a';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    for (let y = 0; y < height; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    for (let x = 0; x < width; x += 24) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }
    ctx.restore();
};

export const renderWatercolorBackground = (ctx, width, height) => {
    ctx.save();
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, '#f7e8d0');
    grad.addColorStop(0.5, '#e0f7fa');
    grad.addColorStop(1, '#fce4ec');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
};

export const renderGradientBackground = (ctx, width, height) => {
    ctx.save();
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, '#f8fafc');
    grad.addColorStop(1, '#dbeafe');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
};

export const renderTextureBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fffbe7';
    ctx.fillRect(0, 0, width, height);
    // Add subtle noise
    for (let i = 0; i < 2000; i++) {
        ctx.globalAlpha = 0.03;
        ctx.fillStyle = '#e2e2d2';
        const x = Math.random() * width;
        const y = Math.random() * height;
        ctx.beginPath();
        ctx.arc(x, y, Math.random() * 0.7 + 0.3, 0, 2 * Math.PI);
        ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
};

export const renderGalaxyBackground = (ctx, width, height, cache) => {
    // Only regenerate stars if canvas size changes or cache is empty
    if (
        cache.width !== width ||
        cache.height !== height ||
        !cache.stars.length
    ) {
        const numStars = 200;
        const stars = [];
        for (let i = 0; i < numStars; i++) {
            stars.push({
                x: Math.random() * width,
                y: Math.random() * height,
                r: Math.random() * 1.2 + 0.2,
                a: Math.random() * 0.7 + 0.2
            });
        }
        cache.width = width;
        cache.height = height;
        cache.stars = stars;
    }
    ctx.save();
    ctx.fillStyle = '#181c2f';
    ctx.fillRect(0, 0, width, height);
    for (const star of cache.stars) {
        ctx.globalAlpha = star.a;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, 2 * Math.PI);
        ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
};

export const renderDotsMinimalBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#d1d5db';
    for (let y = 16; y < height; y += 48) {
        for (let x = 16; x < width; x += 48) {
            ctx.beginPath();
            ctx.arc(x, y, 1, 0, 2 * Math.PI);
            ctx.fill();
        }
    }
    ctx.restore();
};

export const renderKanbanBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2;
    const numCols = 4;
    for (let i = 1; i < numCols; i++) {
        const x = (width / numCols) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }
    ctx.restore();
};

export const renderCalendarBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    
    const rows = 6, cols = 7; // 6 weeks, 7 days
    const cellW = width / cols, cellH = height / rows;
    const headerHeight = 24; // Fixed header height instead of responsive
    const actualCellH = (height - headerHeight) / rows;
    
    // Get current date for highlighting
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Generate calendar for current month
    const firstDay = new Date(currentYear, currentMonth, 1);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    // Day abbreviations
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Draw header background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, headerHeight);
    
    // Draw day name headers with fixed font size
    ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    for (let col = 0; col < cols; col++) {
        const x = col * cellW + cellW / 2;
        const y = headerHeight / 2;
        ctx.fillText(dayNames[col], x, y);
    }
    
    // Draw weekend background (Saturday and Sunday)
    ctx.fillStyle = '#f9fafb';
    // Sunday (column 0)
    ctx.fillRect(0, headerHeight, cellW, height - headerHeight);
    // Saturday (column 6)  
    ctx.fillRect(6 * cellW, headerHeight, cellW, height - headerHeight);
    
    // Draw grid lines
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    
    // Vertical lines
    for (let i = 1; i < cols; i++) {
        ctx.beginPath();
        ctx.moveTo(i * cellW, 0);
        ctx.lineTo(i * cellW, height);
        ctx.stroke();
    }
    
    // Horizontal lines
    // Header separator (thicker)
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#d1d5db';
    ctx.beginPath();
    ctx.moveTo(0, headerHeight);
    ctx.lineTo(width, headerHeight);
    ctx.stroke();
    
    // Regular horizontal lines
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#e5e7eb';
    for (let j = 1; j <= rows; j++) {
        const y = headerHeight + j * actualCellH;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    
    // Draw date numbers with fixed font size
    ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    let dateNum = 1;
    
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            // Skip cells before the first day of the month
            if (row === 0 && col < startDayOfWeek) {
                continue;
            }
            
            // Stop after the last day of the month
            if (dateNum > daysInMonth) {
                break;
            }
            
            const x = col * cellW + 6; // 6px padding from left
            const y = headerHeight + row * actualCellH + 4; // 4px padding from top
            
            // Highlight today's date
            if (dateNum === currentDay) {
                ctx.save();
                ctx.fillStyle = '#dbeafe'; // Light blue background
                ctx.fillRect(col * cellW + 2, headerHeight + row * actualCellH + 2, cellW - 4, actualCellH - 4);
                ctx.strokeStyle = '#3b82f6';
                ctx.lineWidth = 2;
                ctx.strokeRect(col * cellW + 2, headerHeight + row * actualCellH + 2, cellW - 4, actualCellH - 4);
                ctx.restore();
                
                // Today's number in blue with bold font
                ctx.fillStyle = '#1d4ed8';
                ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
            } else {
                // Regular date styling
                ctx.fillStyle = '#374151';
                ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
            }
            
            ctx.fillText(dateNum.toString(), x, y);
            dateNum++;
        }
        
        // Break if we've written all days
        if (dateNum > daysInMonth) {
            break;
        }
    }
    
    ctx.restore();
};

export const renderMindmapBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = 'rgba(100,100,200,0.12)';
    for (let i = 0; i < 8; i++) {
        const x = (width / 8) * (i + 0.5);
        for (let j = 0; j < 4; j++) {
            const y = (height / 4) * (j + 0.5);
            ctx.beginPath();
            ctx.arc(x, y, 40, 0, 2 * Math.PI);
            ctx.stroke();
        }
    }
    ctx.restore();
};

export const renderSeyesBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#b3c6e7';
    ctx.lineWidth = 1;
    for (let y = 0; y < height; y += 8) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    for (let y = 0; y < height; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(56, 0);
    ctx.lineTo(56, height);
    ctx.stroke();
    ctx.restore();
};

export const renderMandalaBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#e0e7ef';
    ctx.lineWidth = 1;
    const cx = width / 2, cy = height / 2;
    for (let i = 0; i < 24; i++) {
        const angle = (i / 24) * 2 * Math.PI;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * Math.min(width, height) / 2, cy + Math.sin(angle) * Math.min(width, height) / 2);
        ctx.stroke();
    }
    for (let r = 40; r < Math.min(width, height) / 2; r += 40) {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, 2 * Math.PI);
        ctx.stroke();
    }
    ctx.restore();
};

export const renderIsometricBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    
    // Isometric grid parameters
    const gridColor = '#7bb3e0'; // darker blue for better visibility
    const lineWidth = 1;
    const triangleSize = 32; // Size of equilateral triangles
    const triangleHeight = triangleSize * Math.sqrt(3) / 2; // Height of equilateral triangle
    
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = 0.7; // Increased from 0.5 to 0.7 for better visibility

    // Rotate the entire coordinate system 90 degrees clockwise
    ctx.translate(width, 0);
    ctx.rotate(Math.PI / 2);

    // Draw individual triangles in a tessellation pattern
    // The vertical lines will emerge naturally from triangle bases touching
    
    const drawTriangle = (x1, y1, x2, y2, x3, y3) => {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.lineTo(x3, y3);
        ctx.closePath();
        ctx.stroke();
    };
    
    // Calculate grid spacing (note: dimensions are swapped due to rotation)
    const colWidth = triangleSize / 2; // Half the triangle width for column spacing
    const rowHeight = triangleHeight; // Full triangle height for row spacing
    
    // Draw triangular tessellation (using height/width swapped due to rotation)
    for (let row = 0; row * rowHeight < width + triangleHeight * 2; row++) {
        for (let col = 0; col * colWidth < height + triangleSize; col++) {
            const baseX = col * colWidth;
            const baseY = row * rowHeight;
            
            // Determine triangle orientation based on position
            const isUpward = (row + col) % 2 === 0;
            
            if (isUpward) {
                // Upward pointing triangle (△)
                const x1 = baseX; // Left base corner
                const y1 = baseY + triangleHeight; // Bottom
                const x2 = baseX + triangleSize; // Right base corner  
                const y2 = baseY + triangleHeight; // Bottom
                const x3 = baseX + triangleSize / 2; // Top point (center)
                const y3 = baseY; // Top
                
                drawTriangle(x1, y1, x2, y2, x3, y3);
            } else {
                // Downward pointing triangle (▽)
                const x1 = baseX; // Left top corner
                const y1 = baseY; // Top
                const x2 = baseX + triangleSize; // Right top corner
                const y2 = baseY; // Top  
                const x3 = baseX + triangleSize / 2; // Bottom point (center)
                const y3 = baseY + triangleHeight; // Bottom
                
                drawTriangle(x1, y1, x2, y2, x3, y3);
            }
        }
    }

    ctx.restore();
};

export const renderHexBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#b6b6b6';
    ctx.lineWidth = 0.7;
    
    // Hexagon parameters for proper tessellation
    const hexRadius = 20; // Radius from center to vertex
    const hexWidth = hexRadius * Math.sqrt(3); // Width of hexagon (flat-to-flat distance)
    const hexHeight = hexRadius * 2; // Height of hexagon (point-to-point distance)
    
    // Spacing for proper tessellation where hexagons just touch
    const horizSpacing = hexWidth; // Horizontal distance between centers (edge-to-edge)
    const vertSpacing = hexHeight * 0.75; // Vertical distance (3/4 of height for interlocking)
    const rowOffset = hexWidth / 2; // Offset alternating rows by half width
    
    // Draw hexagonal grid with proper tessellation
    for (let row = 0; row * vertSpacing < height + hexHeight; row++) {
        for (let col = 0; col * horizSpacing < width + hexWidth; col++) {
            // Offset every other row by half the horizontal spacing
            const xOffset = (row % 2) * rowOffset;
            const centerX = col * horizSpacing + xOffset + hexRadius;
            const centerY = row * vertSpacing + hexRadius;
            
            // Draw hexagon (rotated 30 degrees so flat edge is horizontal)
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const angle = (Math.PI / 3) * i + (Math.PI / 6); // Add π/6 to rotate 30 degrees
                const x = centerX + hexRadius * Math.cos(angle);
                const y = centerY + hexRadius * Math.sin(angle);
                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            }
            ctx.closePath();
            ctx.stroke();
        }
    }
    ctx.restore();
};

export const renderCornellBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#c1c7d0';
    ctx.lineWidth = 1;
    // Main lines
    for (let y = 40; y < height; y += 32) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    // Cue column
    ctx.strokeStyle = '#f59e42';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(220, 0);
    ctx.lineTo(220, height);
    ctx.stroke();
    // Summary row
    ctx.strokeStyle = '#f59e42';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, height - 200);
    ctx.lineTo(width, height - 200);
    ctx.stroke();
    ctx.restore();
};

export const renderMusicBackground = (ctx, width, height) => {
    ctx.save();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1.4;
    const staffSpacing = 90; // wider space between staves
    const lineSpacing = 14;  // wider space between lines
    for (let y = 40; y < height; y += staffSpacing) {
        for (let l = 0; l < 5; l++) {
            ctx.beginPath();
            ctx.moveTo(32, y + l * lineSpacing);
            ctx.lineTo(width - 32, y + l * lineSpacing);
            ctx.stroke();
        }
    }
    ctx.restore();
};

export const renderLinedBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#c1c7d0';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const lineSpacing = 24;
    for (let y = lineSpacing; y < height; y += lineSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(80, 0);
    ctx.lineTo(80, height);
    ctx.stroke();
    ctx.restore();
};

export const renderLinedBlackBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const lineSpacing = 24;
    for (let y = lineSpacing; y < height; y += lineSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(80, 0);
    ctx.lineTo(80, height);
    ctx.stroke();
    ctx.restore();
};

export const renderLinedWideBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const lineSpacing = 36;
    for (let y = lineSpacing; y < height; y += lineSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(80, 0);
    ctx.lineTo(80, height);
    ctx.stroke();
    ctx.restore();
};

export const renderLinedExtraWideBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const lineSpacing = 48;
    for (let y = lineSpacing; y < height; y += lineSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(80, 0);
    ctx.lineTo(80, height);
    ctx.stroke();
    ctx.restore();
};

export const renderGridBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#c1c7d0';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const gridSpacing = 20;
    for (let x = gridSpacing; x < width; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }
    for (let y = gridSpacing; y < height; y += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.restore();
};

export const renderGridWideBackground = (ctx, width, height) => {
    ctx.save();
    ctx.strokeStyle = '#c1c7d0';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.8;
    const gridSpacing = 30;
    for (let x = gridSpacing; x < width; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }
    for (let y = gridSpacing; y < height; y += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    ctx.restore();
};

export const renderDotBackground = (ctx, width, height) => {
    ctx.save();
    const dotSpacing = 20;
    const dotSize = 1.5;
    ctx.fillStyle = '#c1c7d0';
    ctx.globalAlpha = 0.6;
    for (let x = dotSpacing; x < width; x += dotSpacing) {
        for (let y = dotSpacing; y < height; y += dotSpacing) {
            ctx.beginPath();
            ctx.arc(x, y, dotSize, 0, 2 * Math.PI);
            ctx.fill();
        }
    }
    ctx.restore();
};

export const renderDotWideBackground = (ctx, width, height) => {
    ctx.save();
    const dotSpacing = 30;
    const dotSize = 1.5;
    ctx.fillStyle = '#c1c7d0';
    ctx.globalAlpha = 0.6;
    for (let x = dotSpacing; x < width; x += dotSpacing) {
        for (let y = dotSpacing; y < height; y += dotSpacing) {
            ctx.beginPath();
            ctx.arc(x, y, dotSize, 0, 2 * Math.PI);
            ctx.fill();
        }
    }
    ctx.restore();
};

export const renderLeavesBackground = (ctx, width, height) => {
    ctx.save();
    ctx.globalAlpha = 0.18;
    const leafCountX = Math.ceil(width / 220);
    const leafCountY = Math.ceil(height / 220);
    const leafLength = 120;
    const leafWidth = 60;
    ctx.strokeStyle = '#b0b0b0';
    ctx.lineWidth = 2;
    for (let ix = 0; ix < leafCountX; ix++) {
        for (let iy = 0; iy < leafCountY; iy++) {
            const baseX = ix * (width / leafCountX) + (width / leafCountX) / 2;
            const baseY = iy * (height / leafCountY) + (height / leafCountY) / 2;
            const offsetX = (Math.random() - 0.5) * 60;
            const offsetY = (Math.random() - 0.5) * 60;
            const x = baseX + offsetX;
            const y = baseY + offsetY;
            const angle = Math.random() * 2 * Math.PI;
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.quadraticCurveTo(leafLength / 2, -leafWidth, leafLength, 0);
            ctx.quadraticCurveTo(leafLength / 2, leafWidth, 0, 0);
            ctx.closePath();
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(leafLength, 0);
            ctx.strokeStyle = '#b0b0b0';
            ctx.lineWidth = 1.2;
            ctx.stroke();
            for (let v = 1; v <= 3; v++) {
                const t = v / 4;
                ctx.beginPath();
                ctx.moveTo(leafLength * t, 0);
                ctx.lineTo(leafLength * t - 12, -leafWidth * (0.5 - t * 0.7));
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(leafLength * t, 0);
                ctx.lineTo(leafLength * t - 12, leafWidth * (0.5 - t * 0.7));
                ctx.stroke();
            }
            ctx.restore();
        }
    }
    ctx.restore();
};

// Background renderer mapping
export const backgroundRenderers = {
    'white': renderWhiteBackground,
    'blueprint': renderBlueprintBackground,
    'watercolor': renderWatercolorBackground,
    'gradient': renderGradientBackground,
    'texture': renderTextureBackground,
    'galaxy': renderGalaxyBackground,
    'dots-minimal': renderDotsMinimalBackground,
    'kanban': renderKanbanBackground,
    'calendar': renderCalendarBackground,
    'mindmap': renderMindmapBackground,
    'seyes': renderSeyesBackground,
    'mandala': renderMandalaBackground,
    'isometric': renderIsometricBackground,
    'hex': renderHexBackground,
    'cornell': renderCornellBackground,
    'music': renderMusicBackground,
    'lined': renderLinedBackground,
    'lined-black': renderLinedBlackBackground,
    'lined-wide': renderLinedWideBackground,
    'lined-extra-wide': renderLinedExtraWideBackground,
    'grid': renderGridBackground,
    'grid-wide': renderGridWideBackground,
    'dot': renderDotBackground,
    'dot-wide': renderDotWideBackground,
    'leaves': renderLeavesBackground
};
